import { isDefinedError } from "@orpc/client"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Loader2, RefreshCw } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	AttentionCount,
	closeStatusPresentation,
	ID_LINK_CLASS,
	Money,
	QueryErrorLine,
	Segmented,
	SkeletonRows,
	StatusView,
	TEXT_LINK_CLASS,
} from "@/components/accounting/accounting-ui"
import {
	EMPTY_VALUE,
	formatInstant,
	monthStatusPresentation,
} from "@/components/accounting/reconciliation-format"
import {
	KeyValueItem,
	KeyValueList,
	PageHeader,
	Panel,
	Section,
	SectionStack,
	Toolbar,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { StatusDot, StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	type ReconciliationRegion,
	type TriggerLedgerSyncInput,
	useLedgerSyncStatus,
	useReconciliationMonths,
	useTriggerLedgerSync,
} from "@/queries/marketplace/use-stripe-reconciliation"

export const Route = createFileRoute("/accounting/reconciliation/")({
	component: ReconciliationPage,
})

const REGIONS: ReconciliationRegion[] = ["EU", "NA"]

const parisDayFmt = new Intl.DateTimeFormat("en-CA", {
	timeZone: "Europe/Paris",
	year: "numeric",
	month: "2-digit",
	day: "2-digit",
})

function RunStatus({ status }: { status: "running" | "succeeded" | "failed" }) {
	if (status === "failed") return <StatusTag tone="bad">Failed</StatusTag>
	if (status === "succeeded") return <StatusDot tone="ok">Succeeded</StatusDot>
	return <StatusDot tone="info">Running</StatusDot>
}

function ReconciliationPage() {
	const [region, setRegion] = React.useState<ReconciliationRegion>("EU")
	const status = useLedgerSyncStatus(region)
	const months = useReconciliationMonths(region)
	const trigger = useTriggerLedgerSync()
	const [rangeFrom, setRangeFrom] = React.useState("2026-01-01")
	const [rangeTo, setRangeTo] = React.useState(parisDayFmt.format(new Date()))

	const enqueue = async (input: Omit<TriggerLedgerSyncInput, "region">) => {
		const { error, data } = await trigger.mutateAsync({ region, ...input })
		if (error) {
			if (isDefinedError(error) && error.code === "SYNC_ALREADY_RUNNING") {
				toast.error(`A sync is already running for ${region}.`)
				return
			}
			if (isDefinedError(error) && error.code === "INVALID_WINDOW") {
				toast.error("The backfill range must start before it ends.")
				return
			}
			if (isDefinedError(error) && error.code === "ENQUEUE_FAILED") {
				toast.error(`The queue refused the sync: ${error.data.message}`)
				return
			}
			toast.error(
				error instanceof Error
					? `Could not enqueue the sync: ${error.message}`
					: "Could not enqueue the sync.",
			)
			return
		}
		toast.success(
			input.mode === "range"
				? `Backfill enqueued for ${region} (message ${data.messageId}).`
				: `Incremental sync enqueued for ${region}.`,
		)
	}

	const startBackfill = () => {
		enqueue({ mode: "range", fromDay: rangeFrom, toDay: rangeTo })
	}

	const run = status.data?.lastRun
	const anchor = status.data?.anchor

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Stripe reconciliation"
				description="The persistent copy of every Stripe balance transaction, tied to the live Stripe balance per currency and balance type. Days are Europe/Paris."
				actions={
					<Segmented
						ariaLabel="Region"
						value={region}
						onChange={setRegion}
						options={REGIONS.map((r) => ({ value: r, label: r }))}
					/>
				}
			/>

			<SectionStack>
				<Section
					label="Ledger sync"
					action={
						<div className="flex items-center gap-2">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => status.refetch()}
								disabled={status.isFetching}
							>
								<RefreshCw />
								Refresh
							</Button>
							<Button
								size="sm"
								onClick={() => enqueue({ mode: "incremental" })}
								disabled={trigger.isPending || status.data?.running}
							>
								{trigger.isPending ? (
									<Loader2 className="animate-spin" />
								) : null}
								Sync now
							</Button>
						</div>
					}
				>
					{status.isLoading ? (
						<Panel>
							<SkeletonRows rows={3} />
						</Panel>
					) : status.isError ? (
						<QueryErrorLine what="the sync status" error={status.error} />
					) : (
						<KeyValueList>
							<KeyValueItem label="Synced through">
								{formatInstant(status.data?.syncedThrough)}
							</KeyValueItem>
							<KeyValueItem label="Last run">
								{run ? (
									<div className="flex flex-col gap-0.5">
										<span className="flex items-center gap-3">
											<RunStatus status={run.status} />
											<span className="font-mono text-meta">{run.mode}</span>
										</span>
										<span>
											{formatInstant(run.startedAt)} →{" "}
											{run.finishedAt
												? formatInstant(run.finishedAt)
												: "running"}
										</span>
										<span className="text-muted-foreground">
											{run.pages} pages, {run.btsSeen} seen, {run.btsInserted}{" "}
											inserted, {run.btsUpdated} updated
											{run.integrityViolations > 0 ? (
												<span className="text-bad">
													, {run.integrityViolations} write-once violations
												</span>
											) : null}
										</span>
										{run.error ? (
											<span className="text-bad">{run.error}</span>
										) : null}
									</div>
								) : (
									<span className="text-meta">Never synced</span>
								)}
							</KeyValueItem>
							<KeyValueItem label="Anchor">
								{anchor ? (
									<span className="flex flex-wrap items-center gap-3">
										{anchor.balanced ? (
											<StatusDot tone="ok">Ties to Stripe</StatusDot>
										) : (
											<StatusTag tone="bad">Mismatch</StatusTag>
										)}
										<span className="text-meta">
											checked {formatInstant(anchor.checkedAt)}
										</span>
									</span>
								) : (
									<span className="text-meta">{EMPTY_VALUE}</span>
								)}
							</KeyValueItem>
						</KeyValueList>
					)}

					{anchor ? (
						<Panel>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Currency</TableHead>
										<TableHead>Balance type</TableHead>
										<TableHead className="text-right">Ledger Σ net</TableHead>
										<TableHead className="text-right">
											Stripe available
										</TableHead>
										<TableHead className="text-right">Stripe pending</TableHead>
										<TableHead className="text-right">Delta</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{anchor.entries.map((entry) => (
										<TableRow key={`${entry.currency}-${entry.balanceType}`}>
											<TableCell className="font-mono">
												{entry.currency}
											</TableCell>
											<TableCell className="font-mono text-muted-foreground">
												{entry.balanceType}
											</TableCell>
											<TableCell className="text-right">
												<Money
													cents={entry.ledgerNet}
													currency={entry.currency}
												/>
											</TableCell>
											<TableCell className="text-right text-muted-foreground">
												<Money
													cents={entry.stripeAvailable}
													currency={entry.currency}
												/>
											</TableCell>
											<TableCell className="text-right text-muted-foreground">
												<Money
													cents={entry.stripePending}
													currency={entry.currency}
												/>
											</TableCell>
											<TableCell className="text-right font-medium">
												<Money
													cents={entry.delta}
													currency={entry.currency}
													className={
														entry.delta === 0 ? "text-meta" : "text-bad"
													}
												/>
											</TableCell>
										</TableRow>
									))}
									{anchor.warnings.map((warning) => (
										<TableRow key={`warn-${warning.currency}`}>
											<TableCell className="font-mono">
												{warning.currency}
											</TableCell>
											<TableCell className="font-mono text-muted-foreground">
												{warning.section}
											</TableCell>
											<TableCell colSpan={3}>
												<StatusTag tone="bad">
													Not rolled forward by the ledger
												</StatusTag>
											</TableCell>
											<TableCell className="text-right font-medium">
												<Money
													cents={warning.amount}
													currency={warning.currency}
													className="text-bad"
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Panel>
					) : null}
				</Section>

				<Section label="Backfill">
					<p className="text-muted-foreground">
						Both dates are Europe/Paris days, inclusive. A backfill re-reads
						every balance transaction of the range in day slices and resumes
						from the last committed day if it fails. Write-once columns are
						never rewritten.
					</p>
					<Toolbar>
						<label htmlFor="range-from" className="text-meta">
							From
						</label>
						<Input
							id="range-from"
							type="date"
							value={rangeFrom}
							onChange={(e) => setRangeFrom(e.target.value)}
							className="w-40"
						/>
						<label htmlFor="range-to" className="text-meta">
							to (inclusive)
						</label>
						<Input
							id="range-to"
							type="date"
							value={rangeTo}
							onChange={(e) => setRangeTo(e.target.value)}
							className="w-40"
						/>
						<Button
							variant="outline"
							onClick={startBackfill}
							disabled={trigger.isPending || status.data?.running}
						>
							Backfill range
						</Button>
					</Toolbar>
				</Section>

				<Section label="Months">
					<p className="text-muted-foreground">
						A month is reconciled when the ledger is synced 48 hours past its
						end, the anchor ties, every balance rolls forward and no exception
						dated in it is open.
					</p>
					{months.isError ? (
						<QueryErrorLine what="the months" error={months.error} />
					) : (
						<Panel>
							{months.isLoading ? (
								<SkeletonRows rows={6} />
							) : (months.data?.months.length ?? 0) === 0 ? (
								<p className="px-3 py-2 text-muted-foreground">
									No months yet.
								</p>
							) : (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Month</TableHead>
											<TableHead>Status</TableHead>
											<TableHead className="text-right">
												Balance transactions
											</TableHead>
											<TableHead className="text-right">
												Open (Stripe / DB)
											</TableHead>
											<TableHead className="text-right">
												Closing per balance
											</TableHead>
											<TableHead>Close</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{(months.data?.months ?? []).map((month) => {
											const open =
												month.openStripeExceptions + month.openDbExceptions
											return (
												<TableRow key={month.period}>
													<TableCell>
														<Link
															to="/accounting/reconciliation/$region/$period"
															params={{
																region: month.region,
																period: month.period,
															}}
															className={ID_LINK_CLASS}
														>
															{month.period}
														</Link>
													</TableCell>
													<TableCell>
														<StatusView
															presentation={monthStatusPresentation(
																month.status,
															)}
														/>
													</TableCell>
													<TableCell className="text-right tabular-nums">
														{month.balanceTransactions}
													</TableCell>
													<TableCell className="text-right">
														{open > 0 ? (
															<AttentionCount count={open}>
																{month.openStripeExceptions} /{" "}
																{month.openDbExceptions}
															</AttentionCount>
														) : (
															<span className="tabular-nums text-meta">
																0 / 0
															</span>
														)}
													</TableCell>
													<TableCell className="text-right">
														{month.closing.length === 0 ? (
															<span className="text-meta">{EMPTY_VALUE}</span>
														) : (
															<div className="flex flex-col items-end">
																{month.closing.map((block) => (
																	<span
																		key={`${block.currency}-${block.balanceType}`}
																		className="flex items-baseline gap-3"
																	>
																		<span className="text-xs text-meta">
																			{block.currency}
																			{block.balanceType ===
																			"refund_and_dispute_prefunding"
																				? " reserve"
																				: ""}
																		</span>
																		<Money
																			cents={block.closing}
																			currency={block.currency}
																		/>
																	</span>
																))}
															</div>
														)}
													</TableCell>
													<TableCell>
														{month.closeId ? (
															<Link
																to="/accounting/monthly-closes/$closeId"
																params={{ closeId: month.closeId }}
																className={TEXT_LINK_CLASS}
															>
																{month.closeStatus ? (
																	<StatusView
																		presentation={closeStatusPresentation(
																			month.closeStatus,
																		)}
																		className="text-inherit"
																	/>
																) : (
																	"Close"
																)}
															</Link>
														) : (
															<span className="text-meta">No close yet</span>
														)}
													</TableCell>
												</TableRow>
											)
										})}
									</TableBody>
								</Table>
							)}
						</Panel>
					)}
				</Section>
			</SectionStack>
		</div>
	)
}
